import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from 'lucide-react';
import { businessInfo, faqs } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission - will be replaced with actual backend call
    console.log('Form submitted:', formData);
    toast({
      title: "Quote Request Received!",
      description: "We'll contact you within 24 hours to discuss your project.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="contact-page">
      <SEO 
        title="Contact SBF Home Improvements | Free Estimates | (724) 420-6192 | Fayette County, PA"
        description="Get a free estimate for your home improvement project. Call (724) 420-6192 or email sbfhomeimprovements20@gmail.com. Serving Fayette County, Mt Pleasant, Ohiopyle & surrounding areas."
        keywords="contact contractor, free estimate, home improvement quote, Fayette County contractor, Mt Pleasant, PA home improvement, (724) 420-6192"
      />
      {/* Hero Section */}
      <section style={{background: 'var(--bg-page)', padding: '120px 40px 80px'}}>
        <div className="container">
          <h1 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>CONTACT US</h1>
          <p className="body-large" style={{color: 'var(--text-secondary)', maxWidth: '60ch'}}>Get your free estimate today. We're here to help with your home improvement needs.</p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section style={{background: 'var(--bg-card)', padding: '96px 40px'}}>
        <div className="container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px'}}>
            {/* Contact Information */}
            <div>
              <h2 className="heading-3" style={{color: 'var(--text-primary)', marginBottom: '32px', fontSize: '2rem', lineHeight: '1.2'}}>GET IN TOUCH</h2>
              
              <div style={{display: 'flex', flexDirection: 'column', gap: '32px'}}>
                <div style={{display: 'flex', gap: '20px'}}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Phone size={24} color="var(--text-inverse)" />
                  </div>
                  <div>
                    <p className="heading-6" style={{color: 'var(--text-primary)', marginBottom: '4px'}}>Phone</p>
                    <a href={`tel:${businessInfo.phone}`} className="body-medium" style={{color: 'var(--brand-primary)', textDecoration: 'none'}}>{businessInfo.phone}</a>
                  </div>
                </div>

                <div style={{display: 'flex', gap: '20px'}}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Mail size={24} color="var(--text-inverse)" />
                  </div>
                  <div>
                    <p className="heading-6" style={{color: 'var(--text-primary)', marginBottom: '4px'}}>Email</p>
                    <a href={`mailto:${businessInfo.email}`} className="body-medium" style={{color: 'var(--brand-primary)', textDecoration: 'none'}}>{businessInfo.email}</a>
                  </div>
                </div>

                <div style={{display: 'flex', gap: '20px'}}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <MapPin size={24} color="var(--text-inverse)" />
                  </div>
                  <div>
                    <p className="heading-6" style={{color: 'var(--text-primary)', marginBottom: '4px'}}>Address</p>
                    <p className="body-medium" style={{color: 'var(--text-secondary)'}}>{businessInfo.address}</p>
                  </div>
                </div>

                <div style={{display: 'flex', gap: '20px'}}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Clock size={24} color="var(--text-inverse)" />
                  </div>
                  <div>
                    <p className="heading-6" style={{color: 'var(--text-primary)', marginBottom: '4px'}}>Hours</p>
                    <p className="body-medium" style={{color: 'var(--text-secondary)'}}>{businessInfo.hours}</p>
                  </div>
                </div>
              </div>

              <div style={{marginTop: '48px', padding: '24px', background: 'var(--bg-page)', borderRadius: '8px'}}>
                <p className="heading-6" style={{color: 'var(--text-primary)', marginBottom: '12px'}}>Service Areas</p>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                  {businessInfo.serviceAreas.map((area, index) => (
                    <span key={index} className="caption" style={{
                      padding: '6px 12px',
                      background: 'var(--bg-card)',
                      borderRadius: '100px',
                      color: 'var(--brand-primary)',
                      border: '1px solid var(--border-medium)'
                    }}>{area}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Quote Request Form */}
            <div>
              <div style={{background: 'var(--bg-page)', padding: '40px', borderRadius: '8px', border: '1px solid var(--border-medium)'}}>
                <h3 className="heading-4" style={{color: 'var(--text-primary)', marginBottom: '24px'}}>Request a Free Quote</h3>
                <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                  <div>
                    <label className="body-small" style={{color: 'var(--text-primary)', display: 'block', marginBottom: '8px'}}>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label className="body-small" style={{color: 'var(--text-primary)', display: 'block', marginBottom: '8px'}}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label className="body-small" style={{color: 'var(--text-primary)', display: 'block', marginBottom: '8px'}}>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label className="body-small" style={{color: 'var(--text-primary)', display: 'block', marginBottom: '8px'}}>Service Needed *</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="">Select a service...</option>
                      <option value="roofing">Roof Replacement</option>
                      <option value="siding">Siding Installation</option>
                      <option value="flooring">Flooring Installation</option>
                      <option value="repairs">General Repairs</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="body-small" style={{color: 'var(--text-primary)', display: 'block', marginBottom: '8px'}}>Project Details *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      placeholder="Tell us about your project..."
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <button type="submit" className="btn-primary" style={{width: '100%', marginTop: '8px'}}>
                    SUBMIT REQUEST
                  </button>

                  <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px'}}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" />
                    <span className="caption" style={{color: 'var(--text-secondary)'}}>We'll respond within 24 hours</span>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{background: 'var(--bg-page)', padding: '96px 40px'}}>
        <div className="container" style={{maxWidth: '800px'}}>
          <div style={{textAlign: 'center', marginBottom: '60px'}}>
            <h2 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '16px'}}>FREQUENTLY ASKED QUESTIONS</h2>
            <p className="body-medium" style={{color: 'var(--text-secondary)'}}>Quick answers to common questions</p>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="heading-6" style={{color: 'var(--text-primary)', textAlign: 'left'}}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="body-medium" style={{color: 'var(--text-secondary)', lineHeight: '1.6'}}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Contact;